import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from 'aws-amplify/api';
import { pageTitles } from '../data/pageTitles';
import Layout from '../mui-components/Layout';
import Button from '@mui/material/Button';
import { Box, Stack, Typography, Paper } from '@mui/material';
import { useGlobalContext } from '../context/GlobalStore';
import AddWebhookModal from '../mui-components/Webhooks/AddWebhookModal';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';
import apiClient from '../utils/apiClient';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import WebhookTestModal from '../mui-components/Webhooks/WebhookTestModal';
import LoadingDialog from '../mui-components/LoadingDialog';
import Loading from '../mui-components/Loading';
import { HmacSHA256 } from 'crypto-js';

export default function Webhooks({ getAccount }) {
  document.title = pageTitles.webhooks;

  const { state, setAccount } = useGlobalContext();
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState({
    DEVICE_MEASUREMENT: { url: '', exists: false },
    ORDER_UPDATES: { url: '', exists: false },
  });
  const [openAddWebhook, setOpenAddWebhook] = useState(false);
  const [allowAddWebhook, setAllowAddWebhook] = useState(false);
  const [activeWebhookType, setActiveWebhookType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    secret: '',
  });
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [testResponse, setTestResponse] = useState(null);
  const [testSecret, setTestSecret] = useState('');
  const [verificationResult, setVerificationResult] = useState('');

  const skuOptions = [
    { sku: 'PY-200-LTE', productName: 'Pylo Weight Scale 200-LTE' },
    { sku: 'PY-300-LTE', productName: 'Pylo Weight Scale Plus 300-LTE' },
    { sku: 'PY-802-LTE', productName: 'Pylo BPM' },
    { sku: 'PY-900-LTE', productName: 'Pylo BPM 900-LTE' },
    { sku: 'PY-GLU-LTE', productName: 'Pylo Blood Glucose Meter' },
  ];
  const [selectedSku, setSelectedSku] = useState(skuOptions[0].sku);
  const [openTestModal, setOpenTestModal] = useState(false);

  const nullWebhookValues = ["", "N/A", "n/a,", "null", null, undefined];

  useEffect(() => {
    const newWebhooks = {
      DEVICE_MEASUREMENT: { url: '', exists: false },
      ORDER_UPDATES: { url: '', exists: false },
    };

    if (state.account && state.account.webhooks && state.apiKey && state.apiKey.key) {
      const currentPyloAccount = state.account.pylo_accounts?.find(
        (account) => account.api_key === state.apiKey.key
      );

      if (currentPyloAccount) {
        const currentAccountId = currentPyloAccount.id;

        // Find webhooks for each type
        state.account.webhooks.forEach((w) => {
          if (w.customer_id === currentAccountId && w.event_id) {
            const url = w.url;
            const exists = url && !nullWebhookValues.includes(url);
            if (newWebhooks[w.event_id] !== undefined) {
              newWebhooks[w.event_id] = { url: url || '', exists };
            }
          }
        });
      }
    }

    setWebhooks(newWebhooks);
    setAllowAddWebhook(userCanEditWebhooks());

    // Reset test webhook section when API key changes
    setTestResponse(null);
    setTestSecret('');
    setVerificationResult('');

  }, [state.account, state.apiKey]);

  async function createWebhook(e) {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      new URL(newWebhook.url);
    } catch (error) {
      setAlertMessage('Please enter a valid URL (e.g., https://example.com/webhook)');
      setAlertTitle('Invalid URL');
      setOpenErrorAlert(true);
      setIsLoading(false);
      return;
    }
    
    const device_customer_id = state?.account?.pylo_accounts?.find(
      (a) => a.api_key === state.apiKey.key
    )['id'];
    try {

      const response = await apiClient.post(
        '/webhooks',
        {
          action: 'create',
          device_customer_id: device_customer_id,
          webhook_url: newWebhook.url,
          webhook_secret: newWebhook.secret,
          webhook_type: activeWebhookType,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response) {
        // update account data with new webhook information
        setOpenSnackbar(true);
        const isNewWebhook = !webhooks[activeWebhookType]?.exists;
        setSnackbarMessage(`Webhook ${isNewWebhook ? 'created' : 'updated'} successfully`);
        await getAccount(state.token.toString());
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`);
          errorMessage = JSON.parse(body)
        }
      } else {
        // standard error
        errorMessage = error.message;
      }
      setAlertMessage(`Error creating webhook: ${errorMessage}`);
      setAlertTitle('Error');
      setOpenErrorAlert(true);
      setIsLoading(false);
    }
    setNewWebhook({
      url: "",
      secret: "",
    })
    toggleAddWebhookModal();
    setIsLoading(false);
  }

  function userCanEditWebhooks() {
    // Get the current API key from state
    const currentApiKey = state.apiKey?.key;
    
    if (!currentApiKey || !state?.policies) {
      return false;
    }

    // Find the API key entry in policies
    const apiKeyEntry = Object.entries(state.policies).find(([keyName, keyPolicies]) => {
      // Check if any of the sub-keys (actual API keys) match the current API key
      return Object.keys(keyPolicies).some(actualKey => actualKey === currentApiKey);
    });

    if (!apiKeyEntry) {
      return false;
    }

    // Get the policies for the current API key
    const currentKeyPolicies = apiKeyEntry[1][currentApiKey];
    
    if (!currentKeyPolicies || !Array.isArray(currentKeyPolicies)) {
      return false;
    }

    // Check if the current API key has webhook permissions
    return currentKeyPolicies.includes("pylo:CreateWebhook");
  }

  const handleAddWebhook = (e) => {
    createWebhook(e);
  }

  const handleTestWebhook = async () => {
    setIsLoading(true);
    setTestResponse(null);
    setVerificationResult('');

    try {
      const pyloAccount = state?.account?.pylo_accounts?.find(
        (a) => a.api_key === state.apiKey.key
      );

      if (!pyloAccount) {
        // This error will be caught and displayed by the generic handler below
        throw new Error("Could not find a matching Pylo account for the current API key.");
      }
      const device_customer_id = pyloAccount.id;

      const response = await apiClient.post(
        '/webhooks',
        {
          action: 'test',
          device_customer_id: device_customer_id,
          webhook_url: webhooks[activeWebhookType].url,
          selected_sku: selectedSku,
          webhook_type: activeWebhookType,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response) {
        setTestResponse(response);
      }
    } catch (error) {
      let errorMessage = 'An unexpected error occurred.';
      if (error instanceof ApiError && error.response) {
          const { body } = error.response;
          console.log(`Received error response with payload ${body}`);
          const parsedBody = JSON.parse(body);
          errorMessage = parsedBody.message || parsedBody.error || 'Failed to send test webhook.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setAlertMessage(`Error sending test webhook: ${errorMessage}`);
      setAlertTitle('Error');
      setOpenErrorAlert(true);
    } finally {
        setIsLoading(false);
    }
  };

  const handleVerifySignature = () => {
    // This function will now be called by the modal
    if (!testResponse || !testResponse.data || !testSecret) {
      setVerificationResult('Missing data, signature, or secret to verify.');
      return;
    }

    const calculatedSignature = HmacSHA256(testResponse.data, testSecret).toString();

    if (calculatedSignature === testResponse.signature) {
      setVerificationResult(`Signature Verified. Decoded Data: ${testResponse.data}`);
    } else {
      setVerificationResult('Signature Mismatch: Invalid secret or tampered data.');
    }
  };

  const toggleAddWebhookModal = (webhookType) => {
    if (!openAddWebhook) {
      setActiveWebhookType(webhookType);
      const currentUrl = webhooks[webhookType].url || '';
      if (currentUrl && !nullWebhookValues.includes(currentUrl)) {
        setNewWebhook(prevState => ({ ...prevState, url: currentUrl }));
      }
    } else {
      setNewWebhook({ url: '', secret: '' });
      setActiveWebhookType(null);
    }
    setOpenAddWebhook(!openAddWebhook);
  };

  return (
    <Layout title="Webhooks">
      {state.isDeviceLoading ? (
        <Loading />
      ) : (
        <Box sx={{ mt: 4, mb: 4, maxWidth: 900, px: 3 }}>
          <Snackbar
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
          >
            <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }} variant="filled">
              {snackbarMessage}
            </Alert>
          </Snackbar>
          <ErrorAlertModal
            open={openErrorAlert}
            handleClose={() => setOpenErrorAlert(false)}
            message={alertMessage}
            title={alertTitle}
          />
          {openAddWebhook && (
            <AddWebhookModal
              open={true}
              handleClose={toggleAddWebhookModal}
              handleAddWebhook={handleAddWebhook}
              newWebhook={newWebhook}
              setNewWebhook={setNewWebhook}
              isLoading={isLoading}
              webhookUrl={webhooks[activeWebhookType]?.url || ''}
              webhookType={activeWebhookType}
            />
          )}
          <Stack gap={2}>
            {/* Device Measurement Webhook */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderRadius: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Device Measurement
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'monospace',
                    color: webhooks.DEVICE_MEASUREMENT.exists ? 'text.primary' : 'text.disabled',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {webhooks.DEVICE_MEASUREMENT.exists ? webhooks.DEVICE_MEASUREMENT.url : 'Not configured'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                {allowAddWebhook && (
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => toggleAddWebhookModal('DEVICE_MEASUREMENT')}
                  >
                    {webhooks.DEVICE_MEASUREMENT.exists ? 'Edit' : 'Configure'}
                  </Button>
                )}
                {webhooks.DEVICE_MEASUREMENT.exists && allowAddWebhook && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setActiveWebhookType('DEVICE_MEASUREMENT');
                      setOpenTestModal(true);
                    }}
                    disabled={isLoading}
                  >
                    Test
                  </Button>
                )}
                {webhooks.DEVICE_MEASUREMENT.exists && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/webhooks/DEVICE_MEASUREMENT/logs')}
                  >
                    Logs
                  </Button>
                )}
              </Box>
            </Paper>

            {/* Order Updates Webhook */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderRadius: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order Updates (beta)
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'monospace',
                    color: webhooks.ORDER_UPDATES.exists ? 'text.primary' : 'text.disabled',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {webhooks.ORDER_UPDATES.exists ? webhooks.ORDER_UPDATES.url : 'Not configured'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                {allowAddWebhook && (
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => toggleAddWebhookModal('ORDER_UPDATES')}
                  >
                    {webhooks.ORDER_UPDATES.exists ? 'Edit' : 'Configure'}
                  </Button>
                )}
                {webhooks.ORDER_UPDATES.exists && allowAddWebhook && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setActiveWebhookType('ORDER_UPDATES');
                      setOpenTestModal(true);
                    }}
                    disabled={isLoading}
                  >
                    Test
                  </Button>
                )}
                {webhooks.ORDER_UPDATES.exists && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/webhooks/ORDER_UPDATES/logs')}
                  >
                    Logs
                  </Button>
                )}
              </Box>
            </Paper>
          </Stack>
          {openTestModal && (
            <WebhookTestModal
              open={openTestModal}
              handleClose={() => {
                setOpenTestModal(false);
                setActiveWebhookType(null);
                // Reset state when modal closes for a fresh start next time
                setTestResponse(null);
                setTestSecret('');
                setVerificationResult('');
              }}
              webhookUrl={webhooks[activeWebhookType]?.url || ''}
              webhookType={activeWebhookType}
              skuOptions={skuOptions}
              selectedSku={selectedSku}
              setSelectedSku={setSelectedSku}
              onTestWebhook={handleTestWebhook}
              isLoading={isLoading}
              testResponse={testResponse}
              testSecret={testSecret}
              setTestSecret={setTestSecret}
              onVerifySignature={handleVerifySignature}
              verificationResult={verificationResult}
            />
          )}
        </Box>
      )}
    </Layout>
  );
}
